import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { Employee } from '../../types';

interface NotesCellProps {
  row: Employee;
  onChange: (id: number, value: string) => void;
  onBlur: (employee: Employee) => void;
  syncRowHeight: (id: number, textareaHeight: number) => void;
  disabled?: boolean;
}

const NotesCell: React.FC<NotesCellProps> = ({
  row,
  onChange,
  onBlur,
  syncRowHeight,
  disabled = false
}) => {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        const height =
          entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
        syncRowHeight(row.id, height);
      }
    });

    observer.observe(textarea);
    return () => observer.disconnect();
  }, [row.id, syncRowHeight]);

  const autoExpandIfNeeded = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    if (textarea.scrollHeight > textarea.clientHeight) {
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, []);

  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    const newHeight = Math.max(textarea.scrollHeight, textarea.clientHeight);
    textarea.style.height = `${newHeight}px`;
  }, [row.notes]);

  return (
    <textarea
      ref={textareaRef}
      className="grid-notes-textarea"
      value={row.notes ?? ''}
      onChange={e => {
        if (!disabled) {
          onChange(row.id, e.target.value);
          autoExpandIfNeeded();
        }
      }}
      onBlur={() => !disabled && onBlur(row)}
      placeholder={disabled ? '' : 'Enter notes...'}
      readOnly={disabled}
      style={disabled ? { cursor: 'default', backgroundColor: 'transparent' } : undefined}
    />
  );
};

export default NotesCell;

